import React from 'react';

const TermsOfService: React.FC = () => {
  return (
    <div className="app">
      <div className="app-main">
        <div className="stage-container">
          <div className="stage-header">
            <h1>Terms of Service</h1>
            <p>Last updated: {new Date().toLocaleDateString()}</p>
          </div>

          <div style={{ textAlign: 'left', lineHeight: '1.6' }}>
            <h2>1. Acceptance of Terms</h2>
            <p>By accessing or using Race Series Points Manager ("Service"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, do not use the Service.</p>

            <h2>2. Description of Service</h2>
            <p>Race Series Points Manager is a web-based platform that allows users to:</p>
            <ul>
              <li>Upload and manage racing series data</li>
              <li>Calculate and track points across multiple races</li>
              <li>Generate and publish professional race results</li>
              <li>Store and organize racing series information</li>
            </ul>

            <h2>3. User Accounts and Registration</h2>
            <h3>3.1 Account Creation</h3>
            <p>To use certain features, you must create an account using Google authentication. You are responsible for:</p>
            <ul>
              <li>Maintaining the confidentiality of your account credentials</li>
              <li>All activities that occur under your account</li>
              <li>Notifying us of any unauthorized use of your account</li>
            </ul>

            <h3>3.2 Account Requirements</h3>
            <p>You must:</p>
            <ul>
              <li>Be at least 13 years old to use this Service</li>
              <li>Provide accurate and complete information</li>
              <li>Maintain and update your account information</li>
              <li>Use the Service only for lawful purposes</li>
            </ul>

            <h2>4. Acceptable Use</h2>
            <h3>4.1 Permitted Uses</h3>
            <p>You may use the Service to manage legitimate racing series data and publish results for sporting events, competitions, and recreational activities.</p>

            <h3>4.2 Prohibited Uses</h3>
            <p>You may not:</p>
            <ul>
              <li>Upload false, misleading, or fraudulent racing data</li>
              <li>Violate any applicable laws or regulations</li>
              <li>Infringe on others' intellectual property rights</li>
              <li>Upload malicious code, viruses, or harmful content</li>
              <li>Attempt to gain unauthorized access to the Service</li>
              <li>Use the Service to harass, abuse, or harm others</li>
              <li>Scrape or extract data from the Service without permission</li>
            </ul>

            <h2>5. User Content and Data</h2>
            <h3>5.1 Your Data</h3>
            <p>You retain ownership of all racing data and content you upload ("User Content"). By using the Service, you grant us a limited license to:</p>
            <ul>
              <li>Store and process your data to provide the Service</li>
              <li>Generate and publish results as directed by you</li>
              <li>Make technical copies necessary for service operation</li>
            </ul>

            <h3>5.2 Published Results</h3>
            <p>When you choose to publish race results:</p>
            <ul>
              <li>Published results become publicly accessible</li>
              <li>You are responsible for ensuring you have rights to publish participant data</li>
              <li>You should obtain appropriate consents for publishing personal information</li>
              <li>Published results may be indexed by search engines</li>
            </ul>

            <h3>5.3 Data Accuracy</h3>
            <p>You are solely responsible for the accuracy, legality, and appropriateness of your User Content. We do not verify the accuracy of racing data or results.</p>

            <h2>6. Privacy and Data Protection</h2>
            <p>Your privacy is important to us. Our collection and use of your information is governed by our <a href="/privacy-policy" style={{ color: '#2780E3' }}>Privacy Policy</a>, which is incorporated into these Terms by reference.</p>

            <h2>7. Intellectual Property</h2>
            <h3>7.1 Service IP</h3>
            <p>The Service, including its design, functionality, and underlying technology, is owned by us and protected by intellectual property laws.</p>

            <h3>7.2 Trademarks</h3>
            <p>"Race Series Points Manager" and related marks are our trademarks. You may not use our trademarks without written permission.</p>

            <h2>8. Service Availability</h2>
            <h3>8.1 Uptime</h3>
            <p>We strive to maintain high service availability but do not guarantee uninterrupted access. The Service may be temporarily unavailable due to:</p>
            <ul>
              <li>Scheduled maintenance</li>
              <li>Technical issues</li>
              <li>Third-party service disruptions</li>
              <li>Force majeure events</li>
            </ul>

            <h3>8.2 Service Changes</h3>
            <p>We reserve the right to modify, suspend, or discontinue the Service with reasonable notice.</p>

            <h2>9. Fees and Payment</h2>
            <p>The basic Service is currently provided free of charge. We reserve the right to introduce fees for premium features with advance notice.</p>

            <h2>10. Disclaimers and Limitations</h2>
            <h3>10.1 Service Disclaimer</h3>
            <p>THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.</p>

            <h3>10.2 Limitation of Liability</h3>
            <p>TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF DATA, REVENUE, OR PROFITS.</p>

            <h2>11. Indemnification</h2>
            <p>You agree to indemnify and hold us harmless from any claims, damages, or expenses arising from:</p>
            <ul>
              <li>Your use of the Service</li>
              <li>Your User Content</li>
              <li>Your violation of these Terms</li>
              <li>Your violation of others' rights</li>
            </ul>

            <h2>12. Account Termination</h2>
            <h3>12.1 By You</h3>
            <p>You may terminate your account at any time through our <a href="/data-deletion" style={{ color: '#2780E3' }}>Data Deletion</a> process.</p>

            <h3>12.2 By Us</h3>
            <p>We may suspend or terminate your account if you:</p>
            <ul>
              <li>Violate these Terms</li>
              <li>Engage in fraudulent or harmful activities</li>
              <li>Fail to respond to serious security concerns</li>
            </ul>

            <h2>13. Governing Law</h2>
            <p>These Terms are governed by the laws of the United States and the State of California, without regard to conflict of law principles.</p>

            <h2>14. Dispute Resolution</h2>
            <p>Any disputes arising from these Terms or the Service will be resolved through binding arbitration in accordance with the rules of the American Arbitration Association.</p>

            <h2>15. Changes to Terms</h2>
            <p>We may modify these Terms at any time. We will notify users of material changes through the Service or by email. Your continued use constitutes acceptance of the modified Terms.</p>

            <h2>16. Contact Information</h2>
            <p>For questions about these Terms, contact us at:</p>
            <ul>
              <li><strong>Email:</strong> legal@seriespoints.zone4.xyz</li>
              <li><strong>Support:</strong> support@seriespoints.zone4.xyz</li>
            </ul>

            <hr style={{ margin: '2rem 0', border: '1px solid #e2e8f0' }} />
            <p style={{ fontSize: '0.9rem', color: '#718096' }}>
              These Terms of Service are designed to protect both users and service providers while enabling legitimate use of racing series management tools.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService; 